import React, { useState, useEffect } from 'react';
import { Calendar, List, Map, Filter, Search, Download, Edit, ChevronLeft, ChevronRight, RefreshCw, Plus, Home, Anchor, TrendingUp, MapPin, Star, X, CalendarDays, Building, Users, Clock, Wifi } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const AvailabilityManagement = () => {
  const [currentView, setCurrentView] = useState('calendar');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [showRateModal, setShowRateModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedPropertyForRate, setSelectedPropertyForRate] = useState(null);
  
  // Nouveaux états pour les filtres avancés
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState({
    from: new Date(),
    to: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // 30 jours
  });
  const [inventoryFilter, setInventoryFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [capacityFilter, setCapacityFilter] = useState('all');
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Mock data étendu avec sources d'inventory
  const properties = [
    {
      id: 1,
      name: "Villa Côte d'Azur Luxury",
      type: "Villa",
      capacity: 8,
      bedrooms: 4,
      location: "Cannes, France",
      occupancy: 87,
      availability: 13,
      inventorySource: "VillaNovo API",
      sourceColor: "bg-blue-500",
      dailyRate: 2850,
      features: ['Piscine privée', 'Vue mer', 'Chef inclus', 'Wifi'],
      lastSync: '2024-12-15 09:30',
      image: "villa-cannes.jpg"
    },
    {
      id: 2,
      name: "Château Provence Excellence",
      type: "Château",
      capacity: 12,
      bedrooms: 6,
      location: "Saint-Tropez, France",
      occupancy: 65,
      availability: 35,
      inventorySource: "Direct Propriétaire",
      sourceColor: "bg-green-500",
      dailyRate: 4200,
      features: ['Domaine privé', 'Vignoble', 'Staff complet', 'Héliport'],
      lastSync: '2024-12-15 10:15',
      image: "chateau-provence.jpg"
    },
    {
      id: 3,
      name: "Yacht Méditerranée Prestige",
      type: "Yacht",
      capacity: 10,
      bedrooms: 5,
      location: "Monaco",
      occupancy: 45,
      availability: 55,
      inventorySource: "Wimco",
      sourceColor: "bg-purple-500",
      dailyRate: 8500,
      features: ['Équipage inclus', 'Jet-ski', 'Héli-deck', 'Spa'],
      lastSync: '2024-12-15 08:45',
      image: "yacht-monaco.jpg"
    },
    {
      id: 4,
      name: "Villa Toscane Authentique",
      type: "Villa",
      capacity: 6,
      bedrooms: 3,
      location: "Florence, Italie",
      occupancy: 92,
      availability: 8,
      inventorySource: "API Luxury Retreats",
      sourceColor: "bg-orange-500",
      dailyRate: 1850,
      features: ['Oliveraie', 'Cave à vin', 'Piscine', 'Terrasse'],
      lastSync: '2024-12-15 11:20',
      image: "villa-toscane.jpg"
    },
    {
      id: 5,
      name: "Villa Mykonos Exclusive",
      type: "Villa",
      capacity: 14,
      bedrooms: 7,
      location: "Mykonos, Grèce",
      occupancy: 78,
      availability: 22,
      inventorySource: "Airbnb Luxe API",
      sourceColor: "bg-red-500",
      dailyRate: 3200,
      features: ['Plage privée', 'Infinity pool', 'Butler', 'Gym'],
      lastSync: '2024-12-15 07:30',
      image: "villa-mykonos.jpg"
    },
    {
      id: 6,
      name: "Penthouse Manhattan",
      type: "Penthouse",
      capacity: 8,
      bedrooms: 4,
      location: "New York, USA",
      occupancy: 88,
      availability: 12,
      inventorySource: "Direct Propriétaire",
      sourceColor: "bg-green-500",
      dailyRate: 5500,
      features: ['Vue Central Park', 'Terrasse privée', 'Concierge', 'Parking'],
      lastSync: '2024-12-15 12:00',
      image: "penthouse-ny.jpg"
    }
  ];

  // Statistiques avancées
  const quickStats = {
    totalProperties: properties.length,
    averageOccupancy: Math.round(properties.reduce((acc, prop) => acc + prop.occupancy, 0) / properties.length),
    totalCapacity: properties.reduce((acc, prop) => acc + prop.capacity, 0),
    avgDailyRate: `€${Math.round(properties.reduce((acc, prop) => acc + prop.dailyRate, 0) / properties.length).toLocaleString()}`,
    inventorySources: [...new Set(properties.map(p => p.inventorySource))].length
  };

  // Fonction de filtrage avancée
  const getFilteredProperties = () => {
    return properties.filter(property => {
      // Filtre de recherche textuelle
      const matchesSearch = searchQuery === '' || 
        property.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        property.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
        property.type.toLowerCase().includes(searchQuery.toLowerCase());

      // Filtre par source d'inventory
      const matchesInventory = inventoryFilter === 'all' || property.inventorySource === inventoryFilter;

      // Filtre par localisation
      const matchesLocation = locationFilter === 'all' || 
        property.location.toLowerCase().includes(locationFilter.toLowerCase());

      // Filtre par capacité
      const matchesCapacity = capacityFilter === 'all' || 
        (capacityFilter === 'small' && property.capacity <= 6) ||
        (capacityFilter === 'medium' && property.capacity > 6 && property.capacity <= 10) ||
        (capacityFilter === 'large' && property.capacity > 10);

      // Filtre par disponibilité
      const matchesAvailability = availabilityFilter === 'all' ||
        (availabilityFilter === 'high' && property.availability > 50) ||
        (availabilityFilter === 'medium' && property.availability > 20 && property.availability <= 50) ||
        (availabilityFilter === 'low' && property.availability <= 20);

      return matchesSearch && matchesInventory && matchesLocation && matchesCapacity && matchesAvailability;
    });
  };

  // Obtenir les sources d'inventory uniques
  const getInventorySources = () => {
    return [...new Set(properties.map(p => p.inventorySource))];
  };

  // Obtenir les localisations uniques
  const getUniqueLocations = () => {
    return [...new Set(properties.map(p => p.location.split(',')[1]?.trim() || p.location))];
  };

  // Fonctions utilitaires pour le calendrier et les dates
  const formatMonth = (date) => {
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('fr-FR');
  };

  const getStatusForDate = (propertyId, date) => {
    // Simulation des statuts basée sur la date et la propriété
    const dayOfMonth = date.getDate();
    const propertyIndex = propertyId % 4;
    
    if ((dayOfMonth + propertyIndex) % 7 === 0) return 'maintenance';
    if ((dayOfMonth + propertyIndex) % 5 === 0) return 'booked';
    if ((dayOfMonth + propertyIndex) % 6 === 0) return 'on-hold';
    return 'available';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'bg-green-100 hover:bg-green-200 text-green-800';
      case 'booked': return 'bg-red-100 text-red-800';
      case 'on-hold': return 'bg-yellow-100 text-yellow-800';
      case 'maintenance': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-50 text-gray-600';
    }
  };

  const resetFilters = () => {
    setSearchQuery('');
    setInventoryFilter('all');
    setLocationFilter('all');
    setCapacityFilter('all');
    setAvailabilityFilter('all');
    setDateRange({
      from: new Date(),
      to: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
    });
  };

  // Filtrer les propriétés
  const filteredProperties = getFilteredProperties();

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 text-white p-8 relative overflow-hidden">
        <div className="absolute top-0 right-20 text-8xl opacity-10">
          <CalendarDays className="w-32 h-32" />
        </div>
        <h1 className="text-4xl font-light tracking-widest mb-2 relative z-10">GESTION DES DISPONIBILITÉS</h1>
        <p className="text-sm tracking-wider opacity-90 relative z-10">Management avancé des inventaires et disponibilités multi-sources</p>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-yellow-600"></div>
      </div>

      {/* Statistiques globales */}
      <div className="bg-white p-6 shadow-sm">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
          <div className="text-center">
            <div className="text-2xl font-light text-blue-700">{quickStats.totalProperties}</div>
            <div className="text-xs uppercase tracking-wide text-gray-500">Propriétés Totales</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-light text-blue-700">{quickStats.averageOccupancy}%</div>
            <div className="text-xs uppercase tracking-wide text-gray-500">Occupation Moyenne</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-light text-blue-700">{quickStats.totalCapacity}</div>
            <div className="text-xs uppercase tracking-wide text-gray-500">Capacité Totale</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-light text-blue-700">{quickStats.avgDailyRate}</div>
            <div className="text-xs uppercase tracking-wide text-gray-500">Tarif Moyen/Jour</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-light text-blue-700">{quickStats.inventorySources}</div>
            <div className="text-xs uppercase tracking-wide text-gray-500">Sources API</div>
          </div>
        </div>
      </div>

      {/* Système de filtres avancé */}
      <div className="bg-white p-6 border-t shadow-sm">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Recherche principale */}
          <div className="flex-1 min-w-80">
            <label className="text-xs uppercase tracking-wide text-gray-500">Recherche globale</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input 
                placeholder="Nom, lieu, type de propriété..." 
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Période - Date picker */}
          <div className="flex flex-col gap-1">
            <label className="text-xs uppercase tracking-wide text-gray-500">Période de recherche</label>
            <div className="flex gap-2">
              <Input 
                type="date"
                className="w-36"
                value={dateRange.from.toISOString().split('T')[0]}
                onChange={(e) => setDateRange({...dateRange, from: new Date(e.target.value)})}
              />
              <span className="flex items-center text-gray-400">→</span>
              <Input 
                type="date"
                className="w-36"
                value={dateRange.to.toISOString().split('T')[0]}
                onChange={(e) => setDateRange({...dateRange, to: new Date(e.target.value)})}
              />
            </div>
          </div>

          {/* Source d'inventory */}
          <div className="flex flex-col gap-1">
            <label className="text-xs uppercase tracking-wide text-gray-500">Source Inventory</label>
            <Select value={inventoryFilter} onValueChange={setInventoryFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Toutes les sources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les sources</SelectItem>
                {getInventorySources().map(source => (
                  <SelectItem key={source} value={source}>{source}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Localisation */}
          <div className="flex flex-col gap-1">
            <label className="text-xs uppercase tracking-wide text-gray-500">Localisation</label>
            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Tous lieux" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous lieux</SelectItem>
                {getUniqueLocations().map(location => (
                  <SelectItem key={location} value={location}>{location}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Capacité */}
          <div className="flex flex-col gap-1">
            <label className="text-xs uppercase tracking-wide text-gray-500">Capacité</label>
            <Select value={capacityFilter} onValueChange={setCapacityFilter}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Toutes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="small">1-6 pers</SelectItem>
                <SelectItem value="medium">7-10 pers</SelectItem>
                <SelectItem value="large">11+ pers</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Disponibilité */}
          <div className="flex flex-col gap-1">
            <label className="text-xs uppercase tracking-wide text-gray-500">Disponibilité</label>
            <Select value={availabilityFilter} onValueChange={setAvailabilityFilter}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Toutes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="high">&gt; 50%</SelectItem>
                <SelectItem value="medium">20-50%</SelectItem>
                <SelectItem value="low">&lt; 20%</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button variant="outline" onClick={resetFilters}>
              <X className="w-4 h-4 mr-2" />
              Reset
            </Button>
            <Button className="bg-yellow-600 hover:bg-yellow-700 text-black">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        {/* Résultats de filtrage */}
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            {filteredProperties.length} propriété(s) trouvée(s) sur {properties.length} total
          </p>
          <div className="flex gap-2">
            <Button 
              variant={currentView === 'list' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setCurrentView('list')}
            >
              <List className="w-4 h-4 mr-2" />
              Liste
            </Button>
            <Button 
              variant={currentView === 'calendar' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setCurrentView('calendar')}
            >
              <Calendar className="w-4 h-4 mr-2" />
              Calendrier
            </Button>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="p-6">
        {currentView === 'list' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredProperties.map((property) => (
              <Card key={property.id} className="overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1">
                <div className="relative h-48 bg-gradient-to-br from-blue-400 to-blue-600">
                  <div className="absolute top-4 left-4">
                    <Badge className={`${property.sourceColor} text-white`}>
                      {property.inventorySource}
                    </Badge>
                  </div>
                  <div className="absolute top-4 right-4">
                    <Badge variant="outline" className="bg-white/90">
                      {property.type}
                    </Badge>
                  </div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="text-lg font-semibold">{property.name}</h3>
                    <p className="text-sm opacity-90 flex items-center">
                      <MapPin className="w-3 h-3 mr-1" />
                      {property.location}
                    </p>
                  </div>
                </div>

                <CardContent className="p-6">
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center">
                      <Users className="w-5 h-5 mx-auto mb-1 text-gray-600" />
                      <div className="font-semibold">{property.capacity}</div>
                      <div className="text-xs text-gray-500">Invités</div>
                    </div>
                    <div className="text-center">
                      <Building className="w-5 h-5 mx-auto mb-1 text-gray-600" />
                      <div className="font-semibold">{property.bedrooms}</div>
                      <div className="text-xs text-gray-500">Chambres</div>
                    </div>
                    <div className="text-center">
                      <Clock className="w-5 h-5 mx-auto mb-1 text-gray-600" />
                      <div className="font-semibold text-xs">{property.lastSync.split(' ')[1]}</div>
                      <div className="text-xs text-gray-500">Dernière sync</div>
                    </div>
                  </div>

                  <div className="space-y-3 mb-4">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">Occupation</span>
                        <span className="font-medium">{property.occupancy}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-red-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${property.occupancy}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">Disponibilité</span>
                        <span className="font-medium">{property.availability}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${property.availability}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div>
                      <div className="text-lg font-semibold text-blue-700">€{property.dailyRate.toLocaleString()}</div>
                      <div className="text-xs text-gray-500">par nuit</div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" className="bg-blue-700 hover:bg-blue-800 text-white">
                        Gérer
                      </Button>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex flex-wrap gap-1">
                      {property.features.slice(0, 3).map((feature, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {feature}
                        </Badge>
                      ))}
                      {property.features.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{property.features.length - 3}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {currentView === 'calendar' && (
          <Card>
            <CardContent className="p-6">
              <div className="text-center py-12 text-gray-500">
                <CalendarDays className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-semibold text-gray-600 mb-2">Vue Calendrier Avancée</h3>
                <p className="text-gray-500">Interface calendrier détaillée avec gestion des disponibilités par propriété</p>
                <Button className="mt-4 bg-blue-700 hover:bg-blue-800 text-white">
                  Voir la démonstration
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AvailabilityManagement;